import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  LEAD_STATUSES,
  claimFirstAdmin,
  getAdminStatus,
  listLeads,
  renderLeadEmails,
  reportFunnelStats,
  resendDownloadLink,
  updateLead,
  type DownloadRow,
  type LeadEmailRow,
  type LeadRow,
} from "@/lib/admin.functions";

import { PACKAGES } from "@/lib/packages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/leads")({
  head: () => ({
    meta: [
      { title: "Leads | Beheer IT Solutions" },
      { name: "description", content: "Overzicht van alle contactaanvragen per pakket." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Leads | Beheer IT Solutions" },
      { property: "og:description", content: "Overzicht van alle contactaanvragen per pakket." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: LeadsPage,
});

const STATUS_LABEL: Record<string, string> = {
  new: "Nieuw",
  contacted: "Gecontacteerd",
  quoted: "Offerte verstuurd",
  won: "Gewonnen",
  lost: "Verloren",
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" });
}

function LeadsPage() {
  const statusFn = useServerFn(getAdminStatus);
  const status = useQuery({ queryKey: ["admin-status"], queryFn: () => statusFn({}) });

  if (status.isLoading)
    return (
      <Shell>
        <p className="text-muted-foreground">Laden…</p>
      </Shell>
    );
  if (status.isError)
    return (
      <Shell>
        <p className="text-destructive">Kon je toegang niet controleren.</p>
      </Shell>
    );
  if (!status.data?.isAdmin)
    return (
      <Shell>
        <NoAccess
          adminExists={Boolean(status.data?.adminExists)}
          email={status.data?.email ?? ""}
        />
      </Shell>
    );

  return (
    <Shell>
      <LeadsTable />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  async function signOut() {
    await supabase.auth.signOut();
    window.location.href = "/auth";
  }
  return (
    <main className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-8">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Leads</h1>
          <p className="text-sm text-muted-foreground">
            Contactaanvragen, pakketten en verstuurde e-mails.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/templates">E-mailtemplates</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/vitals">Web Vitals</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/pagespeed">Paginascan</Link>
          </Button>

          <Button variant="outline" size="sm" onClick={signOut}>
            Afmelden
          </Button>
        </div>
      </div>
      {children}
    </main>
  );
}

function NoAccess({ adminExists, email }: { adminExists: boolean; email: string }) {
  const claim = useServerFn(claimFirstAdmin);
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => claim({}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-status"] }),
  });

  if (!adminExists) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-lg font-medium">Beheer activeren</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Er is nog geen beheerder. Je bent aangemeld als <strong>{email}</strong>. Claim de
          beheerdersrol om het leadoverzicht te openen.
        </p>
        <Button className="mt-4" onClick={() => m.mutate()} disabled={m.isPending}>
          {m.isPending ? "Bezig…" : "Word beheerder"}
        </Button>
        {m.isError ? (
          <p className="mt-3 text-sm text-destructive">Dat lukte niet. Probeer opnieuw.</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h2 className="text-lg font-medium">Geen toegang</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Het account <strong>{email}</strong> heeft geen beheerdersrechten voor dit overzicht.
      </p>
    </div>
  );
}

/** Conversiewidget voor de rapport-leadmagneet, per taal. */
function ReportFunnelCard() {
  const statsFn = useServerFn(reportFunnelStats);
  const q = useQuery({ queryKey: ["report-funnel"], queryFn: () => statsFn({}) });

  if (q.isLoading || q.isError || !q.data || !q.data.rows.length) return null;

  const pct = (part: number, total: number) =>
    total ? `${Math.round((part / total) * 100)}%` : "—";
  const rows = [...q.data.rows, q.data.totaal];

  return (
    <section className="mb-6 rounded-xl border border-border bg-card p-5">
      <h2 className="text-sm font-medium">
        {/* i18n-ignore */}Voorbeeldrapport — conversie per taal
      </h2>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[520px] text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="py-2 pr-4">Taal</th>
              <th className="py-2 pr-4">Aanvragen</th>
              <th className="py-2 pr-4">Link verstuurd</th>
              <th className="py-2 pr-4">Link geopend</th>
              <th className="py-2">Rapport gedownload</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.lang}
                className={r.lang === "totaal" ? "border-t border-border font-medium" : ""}
              >
                <td className="py-2 pr-4">
                  {r.lang === "totaal" ? "Totaal" : r.lang.toUpperCase()}
                </td>
                <td className="py-2 pr-4">{r.aanvragen}</td>
                <td className="py-2 pr-4">
                  {r.verstuurd}{" "}
                  <span className="text-xs text-muted-foreground">
                    ({pct(r.verstuurd, r.aanvragen)})
                  </span>
                </td>
                <td className="py-2 pr-4">
                  {r.geopend}{" "}
                  <span className="text-xs text-muted-foreground">
                    ({pct(r.geopend, r.aanvragen)})
                  </span>
                </td>
                <td className="py-2">
                  {r.gedownload}{" "}
                  <span className="text-xs text-muted-foreground">
                    ({pct(r.gedownload, r.aanvragen)})
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const QUALITY_LABEL: Record<string, string> = {
  strong: "Sterk",
  normal: "Normaal",
  suspect: "Verdacht",
};

/**
 * Kwaliteitsindicator per lead: het oordeel van de automatische controle
 * (MX-record, externe verificatie, bereikbare website, domeinmatch).
 */
function QualityCell({ lead }: { lead: LeadRow }) {
  const q = lead.lead_quality;
  if (!q) return <span className="text-xs text-muted-foreground">—</span>;
  const tone =
    q === "strong"
      ? "border-emerald-border bg-emerald-surface text-emerald-ink"
      : q === "suspect"
        ? "border-destructive/40 bg-destructive/10 text-destructive"
        : "border-border bg-muted text-muted-foreground";
  return (
    <div className="space-y-1">
      <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}>
        {QUALITY_LABEL[q] ?? q}
      </span>
      <div className="flex flex-wrap gap-1 text-[11px] text-muted-foreground">
        {lead.email_has_mx === false ? <Badge variant="outline">geen MX</Badge> : null}
        {lead.email_verification_status && lead.email_verification_status !== "unknown" ? (
          <Badge variant="outline">{lead.email_verification_status}</Badge>
        ) : null}
        {lead.disposable_email ? <Badge variant="outline">wegwerp</Badge> : null}
        {lead.email_is_free_provider ? <Badge variant="outline">gratis mail</Badge> : null}
        {lead.website_reachable === false ? (
          <Badge variant="outline">site onbereikbaar</Badge>
        ) : null}
        {lead.email_domain_matches_website ? <Badge variant="outline">domein matcht</Badge> : null}
      </div>
    </div>
  );
}

function LeadsTable() {
  const [pkg, setPkg] = useState("");
  const [st, setSt] = useState("");
  const [quality, setQuality] = useState("");
  const [lang, setLang] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<LeadRow | null>(null);
  const [mailFor, setMailFor] = useState<LeadRow | null>(null);

  const list = useServerFn(listLeads);
  const filters = {
    packageId: pkg || undefined,
    status: st || undefined,
    quality: (quality || undefined) as "strong" | "normal" | "suspect" | undefined,
    lang: lang || undefined,
    search: search || undefined,
  };
  const q = useQuery({
    queryKey: ["leads", filters],
    queryFn: () => list({ data: filters }),
  });

  const emailsByLead = useMemo(() => {
    const map = new Map<string, LeadEmailRow[]>();
    for (const e of q.data?.emails ?? []) {
      map.set(e.lead_id, [...(map.get(e.lead_id) ?? []), e]);
    }
    return map;
  }, [q.data]);

  const downloadsByLead = useMemo(() => {
    const map = new Map<string, DownloadRow[]>();
    for (const d of q.data?.downloads ?? []) {
      map.set(d.lead_id, [...(map.get(d.lead_id) ?? []), d]);
    }
    return map;
  }, [q.data]);

  function exportCsv() {
    const rows = q.data?.leads ?? [];
    const head = [
      "Datum",
      "Kwaliteit",
      "Naam",
      "E-mail",
      "GSM",
      "Bedrijf",
      "Website",
      "Pakket",
      "Offerte",
      "Taal",
      "Type",
      "Status",
      "Opvolging",
      "Bericht",
      "Notities",
    ];
    // Waarden die met = + - @ (of tab/CR) beginnen, worden door Excel/Sheets als
    // formule uitgevoerd. Neutraliseren met een voorgevoegde apostrof.
    const esc = (v: string) => {
      const raw = String(v ?? "");
      const safe = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
      return `"${safe.replace(/"/g, '""')}"`;
    };
    const csv = [
      head.map(esc).join(";"),
      ...rows.map((r) =>
        [
          fmtDate(r.created_at),
          QUALITY_LABEL[r.lead_quality ?? ""] ?? "—",
          `${r.first_name} ${r.last_name}`,
          r.email,
          r.gsm,
          r.company,
          r.website,
          r.package_name ?? "",
          r.quote_number ?? "",
          r.lang,
          r.form_type,
          STATUS_LABEL[r.status] ?? r.status,
          r.followup_opt_out
            ? "Uitgeschakeld"
            : r.followup_sent_at
              ? fmtDate(r.followup_sent_at)
              : r.status === "new"
                ? "Gepland"
                : "",
          r.message,
          r.notes,
        ]
          .map(esc)
          .join(";"),
      ),
    ].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const leads = q.data?.leads ?? [];

  return (
    <>
      <ReportFunnelCard />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select
          aria-label="Filter op pakket"
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          value={pkg}
          onChange={(e) => setPkg(e.target.value)}
        >
          <option value="">Alle pakketten</option>
          {PACKAGES.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter op status"
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          value={st}
          onChange={(e) => setSt(e.target.value)}
        >
          <option value="">Alle statussen</option>
          {LEAD_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          /* i18n-ignore */ aria-label="Filter op kwaliteit"
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          value={quality}
          onChange={(e) => setQuality(e.target.value)}
        >
          <option value="">Alle kwaliteiten</option>
          <option value="strong">Sterk</option>
          <option value="normal">Normaal</option>
          <option value="suspect">Verdacht</option>
        </select>
        <select
          aria-label="Filter op taal"
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
        >
          <option value="">Alle talen</option>
          <option value="nl">NL</option>
          <option value="fr">FR</option>
          <option value="en">EN</option>
        </select>
        <Input
          className="h-10 w-56"
          placeholder="Zoek naam, e-mail, bedrijf…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!leads.length}>
          Exporteer CSV ({leads.length})
        </Button>
      </div>

      {q.isLoading ? <p className="text-muted-foreground">Laden…</p> : null}
      {q.isError ? <p className="text-destructive">Kon de leads niet laden.</p> : null}

      {!q.isLoading && !leads.length ? (
        <p className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Nog geen aanvragen die aan deze filters voldoen.
        </p>
      ) : null}

      {leads.length ? (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Datum</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Telefoon</th>
                <th className="px-4 py-3">Kwaliteit</th>
                <th className="px-4 py-3">Pakket</th>
                <th className="px-4 py-3">Offerte</th>
                <th className="px-4 py-3">Mails</th>
                <th className="px-4 py-3">Download</th>
                <th className="px-4 py-3">Herkomst</th>
                <th className="px-4 py-3">Opvolging</th>
                <th className="px-4 py-3">Status</th>

                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => {
                const mails = emailsByLead.get(l.id) ?? [];
                const failed = mails.some((m) => !m.sent);
                return (
                  <tr key={l.id} className="border-t border-border align-top">
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {fmtDate(l.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">
                        {l.first_name} {l.last_name}
                      </div>
                      <div className="text-muted-foreground">{l.email}</div>
                      {l.company ? <div className="text-muted-foreground">{l.company}</div> : null}
                    </td>
                    <td className="px-4 py-3">
                      <PhoneCell value={l.gsm} />
                    </td>
                    <td className="px-4 py-3">
                      <QualityCell lead={l} />
                    </td>
                    <td className="px-4 py-3">
                      {l.package_name ? (
                        <>
                          <div>{l.package_name}</div>
                          <div className="text-muted-foreground">{l.package_price}</div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                      <Badge variant="outline" className="mt-1">
                        {l.form_type}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div>{l.quote_number ?? "—"}</div>
                      {l.quote_pdf_attached ? (
                        <div className="text-xs text-muted-foreground">PDF bijgevoegd</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <span className={failed ? "text-destructive" : "text-muted-foreground"}>
                        {mails.filter((m) => m.sent).length}/{mails.length} verstuurd
                      </span>
                      <button
                        type="button"
                        className="mt-1 block text-xs underline underline-offset-2"
                        onClick={() => setMailFor(l)}
                      >
                        Bekijk mails
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <DownloadCell
                        lead={l}
                        rows={downloadsByLead.get(l.id) ?? []}
                        emails={emailsByLead.get(l.id) ?? []}
                      />
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {l.utm_source || l.utm_medium || l.utm_campaign ? (
                        <div>
                          {[l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join(" · ")}
                        </div>
                      ) : (
                        <div>—</div>
                      )}
                      {l.disposable_email ? (
                        <Badge variant="outline" className="mt-1">
                          wegwerp-e-mail
                        </Badge>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <FollowupCell lead={l} />
                    </td>

                    <td className="px-4 py-3">
                      <StatusCell lead={l} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setOpen(l)}>
                          Details
                        </Button>
                        <Button variant="ghost" size="sm" asChild>
                          <Link to="/admin/leads/$leadId" params={{ leadId: l.id }}>
                            Tijdlijn
                          </Link>
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      <DetailDialog lead={open} onClose={() => setOpen(null)} />
      <MailDialog
        lead={mailFor}
        emails={mailFor ? (emailsByLead.get(mailFor.id) ?? []) : []}
        onClose={() => setMailFor(null)}
      />
    </>
  );
}

/** Toont de funnelstatus van de rapport-leadmagneet + opnieuw versturen. */
function DownloadCell({
  lead,
  rows,
  emails,
}: {
  lead: LeadRow;
  rows: DownloadRow[];
  emails: LeadEmailRow[];
}) {
  const qc = useQueryClient();
  const resend = useServerFn(resendDownloadLink);
  const m = useMutation({
    mutationFn: () => resend({ data: { id: lead.id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
  });

  if (!rows.length) return <span className="text-muted-foreground">—</span>;

  const latest = [...rows].sort((a, b) => b.created_at.localeCompare(a.created_at))[0]!;
  const expired = new Date(latest.expires_at).getTime() < Date.now();

  // Funnel: aanvraag → link verstuurd → link geopend → rapport gedownload.
  const sentAt = emails
    .filter((e) => e.kind === "download" && e.sent)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0]?.created_at;
  const stage: { label: string; at: string } = latest.first_downloaded_at
    ? { label: "Rapport gedownload", at: latest.first_downloaded_at }
    : latest.first_opened_at
      ? { label: "Link geopend", at: latest.first_opened_at }
      : sentAt
        ? { label: "Link verstuurd", at: sentAt }
        : { label: "Aanvraag", at: latest.created_at };

  return (
    <div className="text-xs">
      <div>
        <Badge variant="secondary">{stage.label}</Badge>
      </div>
      <div className="mt-1 text-muted-foreground">{fmtDate(stage.at)}</div>
      <div className={expired ? "text-destructive" : "text-muted-foreground"}>
        {latest.download_count}/{latest.max_downloads} downloads
      </div>
      <div className="text-muted-foreground">
        {expired ? "verlopen" : `geldig tot ${fmtDate(latest.expires_at)}`}
      </div>
      <button
        type="button"
        className="mt-1 underline underline-offset-2 disabled:opacity-60"
        onClick={() => m.mutate()}
        disabled={m.isPending}
      >
        {m.isPending ? "Versturen…" : "Nieuwe link mailen"}
      </button>
      {m.data && !m.data.ok ? <div className="text-destructive">{m.data.error}</div> : null}
      {m.isError ? <div className="text-destructive">Mislukt.</div> : null}
    </div>
  );
}

function StatusCell({ lead }: { lead: LeadRow }) {
  const qc = useQueryClient();
  const upd = useServerFn(updateLead);
  const m = useMutation({
    mutationFn: (status: string) =>
      upd({ data: { id: lead.id, status: status as (typeof LEAD_STATUSES)[number] } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
  });
  return (
    <select
      aria-label="Status van deze lead"
      className="h-9 rounded-md border border-input bg-background px-2 text-sm"
      value={lead.status}
      disabled={m.isPending}
      onChange={(e) => m.mutate(e.target.value)}
    >
      {LEAD_STATUSES.map((s) => (
        <option key={s} value={s}>
          {STATUS_LABEL[s]}
        </option>
      ))}
    </select>
  );
}

function DetailDialog({ lead, onClose }: { lead: LeadRow | null; onClose: () => void }) {
  return <DetailDialogInner lead={lead} onClose={onClose} />;
}

/**
 * Opvolgstatus per lead: leeg zolang de mail nog kan vertrekken,
 * anders de datum van verzending of een expliciete uitschakeling.
 */
/** Telefoonnummer als klikbare bel-link; leeg nummer toont een streepje. */
export function PhoneCell({ value }: { value: string }) {
  const nr = (value ?? "").trim();
  if (!nr) return <span className="text-muted-foreground">—</span>;
  return (
    <a
      href={`tel:${nr.replace(/[^\d+]/g, "")}`}
      className="whitespace-nowrap font-medium text-primary underline-offset-4 hover:underline"
    >
      {nr}
    </a>
  );
}

function FollowupCell({ lead }: { lead: LeadRow }) {
  if (lead.followup_opt_out) return <span className="text-muted-foreground">Uitgeschakeld</span>;
  if (lead.followup_sent_at) {
    return (
      <span className="text-muted-foreground">
        Verstuurd
        <br />
        <span className="text-xs">{fmtDate(lead.followup_sent_at)}</span>
      </span>
    );
  }
  if (lead.status !== "new") return <span className="text-muted-foreground">—</span>;
  return <span className="text-muted-foreground">Gepland</span>;
}

function DetailDialogInner({ lead, onClose }: { lead: LeadRow | null; onClose: () => void }) {
  return (
    <Dialog open={Boolean(lead)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        {lead ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {lead.first_name} {lead.last_name}
              </DialogTitle>
            </DialogHeader>
            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <Item label="E-mail" value={lead.email} />
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Telefoon</dt>
                <dd className="flex items-center gap-2 break-words">
                  <PhoneCell value={lead.gsm} />
                  {lead.gsm ? (
                    <button
                      type="button"
                      onClick={() => void navigator.clipboard?.writeText(lead.gsm)}
                      className="text-xs text-muted-foreground underline-offset-4 hover:underline"
                    >
                      Kopieer
                    </button>
                  ) : null}
                </dd>
              </div>
              <Item label="Bedrijf" value={lead.company} />
              <Item label="Website" value={lead.website} />
              <Item
                label="Adres"
                value={[lead.street, lead.number, lead.postcode, lead.city, lead.country]
                  .filter(Boolean)
                  .join(" ")}
              />
              <Item label="Taal" value={lead.lang} />
              <Item label="Pakket" value={lead.package_name ?? "—"} />
              <Item label="Offertenummer" value={lead.quote_number ?? "—"} />
            </dl>
            <div>
              <h3 className="text-sm font-medium">Bericht</h3>
              <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                {lead.message}
              </p>
            </div>
            <NotesEditor lead={lead} />
            <FollowupToggle lead={lead} />
            <AttachPdfToggle lead={lead} />
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="break-words">{value || "—"}</dd>
    </div>
  );
}

function NotesEditor({ lead }: { lead: LeadRow }) {
  const [value, setValue] = useState(lead.notes ?? "");
  const qc = useQueryClient();
  const upd = useServerFn(updateLead);
  const m = useMutation({
    mutationFn: () => upd({ data: { id: lead.id, notes: value } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
  });
  return (
    <div>
      <h3 className="text-sm font-medium">Interne notities</h3>
      <Textarea
        className="mt-2"
        rows={4}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <Button className="mt-2" size="sm" onClick={() => m.mutate()} disabled={m.isPending}>
        {m.isPending ? "Bewaren…" : "Notitie bewaren"}
      </Button>
      {m.isSuccess ? <span className="ml-3 text-sm text-muted-foreground">Bewaard.</span> : null}
    </div>
  );
}

/** Zet de automatische opvolgmail aan of uit voor deze ene lead. */
function FollowupToggle({ lead }: { lead: LeadRow }) {
  const qc = useQueryClient();
  const upd = useServerFn(updateLead);
  const m = useMutation({
    mutationFn: (v: boolean) => upd({ data: { id: lead.id, followupOptOut: v } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
  });
  const sent = Boolean(lead.followup_sent_at);
  return (
    <div className="rounded-lg border border-border p-3">
      <label className="flex items-start gap-3 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          defaultChecked={lead.followup_opt_out}
          disabled={m.isPending}
          onChange={(e) => m.mutate(e.target.checked)}
        />
        <span>
          <span className="font-medium">Geen opvolgmail</span>
          <span className="mt-1 block text-muted-foreground">
            {sent
              ? `De automatische opvolgmail vertrok al op ${fmtDate(lead.followup_sent_at!)}.`
              : "Automatisch, 48 uur na de aanvraag en enkel zolang de status ‘Nieuw’ is."}
          </span>
        </span>
      </label>
      {m.isSuccess ? <p className="mt-2 text-sm text-muted-foreground">Bewaard.</p> : null}
    </div>
  );
}

/** Zet de PDF-bijlage (offerte/aanvraagbevestiging) aan of uit voor deze ene lead. */
function AttachPdfToggle({ lead }: { lead: LeadRow }) {
  const qc = useQueryClient();
  const upd = useServerFn(updateLead);
  const m = useMutation({
    mutationFn: (v: boolean) => upd({ data: { id: lead.id, attachPdf: v } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
  });
  return (
    <div className="rounded-lg border border-border p-3">
      <label className="flex items-start gap-3 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          defaultChecked={lead.attach_pdf !== false}
          disabled={m.isPending}
          onChange={(e) => m.mutate(e.target.checked)}
        />
        <span>
          <span className="font-medium">PDF-bijlage meesturen</span>
          <span className="mt-1 block text-muted-foreground">
            Bepaalt of de offerte- of aanvraag-PDF als bijlage meegaat met de opvolgmail voor deze
            aanvraag. De eerste mail is al verstuurd en verandert niet meer.
          </span>
        </span>
      </label>
      {m.isSuccess ? <p className="mt-2 text-sm text-muted-foreground">Bewaard.</p> : null}
    </div>
  );
}

function MailDialog({
  lead,
  emails,
  onClose,
}: {
  lead: LeadRow | null;
  emails: LeadEmailRow[];
  onClose: () => void;
}) {
  const render = useServerFn(renderLeadEmails);
  const q = useQuery({
    queryKey: ["lead-emails", lead?.id],
    queryFn: () => render({ data: { id: lead!.id } }),
    enabled: Boolean(lead),
  });
  const [tab, setTab] = useState<"internal" | "confirm" | "followup">("internal");
  const html =
    tab === "internal"
      ? q.data?.internal.html
      : tab === "confirm"
        ? q.data?.confirm.html
        : q.data?.followup.html;

  return (
    <Dialog open={Boolean(lead)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Verstuurde e-mails</DialogTitle>
        </DialogHeader>
        <ul className="space-y-1 text-sm">
          {emails.map((e) => (
            <li key={e.id} className="flex flex-wrap gap-2">
              <Badge variant={e.sent ? "outline" : "destructive"}>
                {e.kind === "internal"
                  ? "Intern"
                  : e.kind === "followup"
                    ? "Opvolging"
                    : "Bevestiging"}
              </Badge>
              <span className="text-muted-foreground">{e.recipient}</span>
              <span>{e.subject}</span>
              <span className="text-muted-foreground">{fmtDate(e.created_at)}</span>
              {e.error ? <span className="text-destructive">{e.error}</span> : null}
            </li>
          ))}
          {!emails.length ? (
            <li className="text-muted-foreground">Geen mailgegevens bewaard.</li>
          ) : null}
        </ul>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={tab === "internal" ? "default" : "outline"}
            onClick={() => setTab("internal")}
          >
            Interne mail
          </Button>
          <Button
            size="sm"
            variant={tab === "confirm" ? "default" : "outline"}
            onClick={() => setTab("confirm")}
          >
            Klantbevestiging
          </Button>
          <Button
            size="sm"
            variant={tab === "followup" ? "default" : "outline"}
            onClick={() => setTab("followup")}
          >
            Opvolgmail
          </Button>
        </div>
        {q.isLoading ? <p className="text-sm text-muted-foreground">Laden…</p> : null}
        {html ? (
          <iframe
            title="E-mailweergave"
            srcDoc={html}
            className="h-[480px] w-full rounded-lg border border-border bg-white"
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
